/**
 * invoicePerRate — gemeinsamer, reiner Per-Satz-Reader für Ausgangsrechnungen.
 *
 * Extrahiert aus `DatevExportPage.tsx` (M8a, §3.4 architecture-m8.md): `reconstructPerRate`
 * lebte modul-privat in der DATEV-Seite. M7-DATEV UND M8-UStVA brauchen identische Logik
 * (Beträge aus `taxSummary` bzw. `items[]`+`documentAllowances`, Kategorie/Auswahl je Satz).
 * Golden Rule „Shared Mapper-Funktionen (kein Copy-Paste)" → hier zentralisiert.
 *
 * Verhaltens-erhaltend: die Logik ist 1:1 aus der DATEV-Seite übernommen; `DatevExportPage`
 * importiert die Funktion von hier (kein Output-Diff, durch DATEV-Regressionstest belegt).
 *
 * Rein: KEINE Firebase-/React-Imports — nur der `InvoiceData`-TYP (compile-time erased)
 * und `eurosToCents`. Trivial unit-testbar.
 *
 * @module invoicePerRate
 */

import { InvoiceData } from '../types/invoiceTypes';
import { eurosToCents } from './moneyUtils';

/**
 * Eine Netto/USt-Zeile zu genau einem Steuersatz (Integer-Cents), rekonstruiert aus
 * einer Ausgangsrechnung. Strukturell kompatibel zu `DatevPerRate` (datevExport.ts);
 * von M7-DATEV und M8-UStVA gemeinsam genutzt.
 */
export interface PerRateLine {
  /** USt-Satz in Prozent (19 | 7 | 0 | …). */
  taxRate: number;
  /** Netto-Bemessungsgrundlage in Integer-Cents (vorzeichen-aware). */
  netCents: number;
  /** USt-Betrag in Integer-Cents (vorzeichen-aware). */
  vatCents: number;
  /** EN-16931 BT-151 Kategorie-Code, falls auf der Position vorhanden; sonst null. */
  taxCategory: string | null;
  /**
   * Präziser VB-1-Auswahl-Schlüssel (`taxCategorySelection`), falls die Position ihn trägt.
   * Hat Vorrang vor `taxCategory` und disambiguiert `E` (§19 vs. §4). null bei Altbestand.
   */
  taxCategorySelection: string | null;
}

/**
 * Rekonstruiert die Netto/USt-Bemessungsgrundlage je Steuersatz (Integer-Cents).
 *
 * Quellen-Priorität:
 *   1. `rawData.calculatedTotals.taxSummary` (falls vorhanden) — enthält bereits
 *      Dokumentrabatte (calculateTotals) und ist die belegtreue Quelle.
 *   2. Sonst Rekonstruktion aus `rawData.items[]` (Σ netPrice×quantity je Satz)
 *      plus Dokumentrabatte/-aufschläge (`documentAllowances`), gespiegelt zu
 *      `calculateTotals`. Skonto bleibt unberücksichtigt (Zahlungsbuchung/M5).
 *
 * @param raw Komplettes (Partial-)`InvoiceData` aus dem Firestore-`invoices`-Dokument.
 * @returns Array `PerRateLine` (kann leer sein, wenn keine Positionen vorliegen).
 */
export function reconstructPerRate(raw: Partial<InvoiceData> | undefined): PerRateLine[] {
  if (!raw) return [];

  // Steuerkategorie + präzise VB-1-Auswahl je Satz aus den Positionen ableiten —
  // wird von BEIDEN Pfaden genutzt (auch wenn die Beträge aus taxSummary kommen),
  // damit explizit gewählte Kategorien (z. B. §12(3)-PV → Z) nicht verloren gehen.
  // Erstes nicht-leeres Vorkommen je Satz gewinnt (analog Bestandsverhalten taxCategory).
  // Hinweis: gemischte VB-1-Kategorien am SELBEN Satz (selten) werden über die erste
  // Position aufgelöst; eine spätere Verfeinerung müsste je (Satz × Auswahl) bucketn.
  const items = Array.isArray(raw.items) ? raw.items : [];
  const catByRate = new Map<number, string | null>();
  const selectionByRate = new Map<number, string | null>();
  for (const item of items) {
    const rate = typeof item.taxRate === 'number' ? item.taxRate : 0;
    // "Erstes nicht-leeres Vorkommen je Satz gewinnt": Map-Eintrag erst dann als
    // final behandeln, wenn ein nicht-null-Wert gefunden wurde. Solange der Eintrag
    // fehlt oder noch null ist, wird er beim nächsten non-null-Vorkommen überschrieben.
    if (!catByRate.has(rate) || catByRate.get(rate) == null) {
      const cat = (item as { taxCategory?: string | null }).taxCategory;
      const catVal = typeof cat === 'string' && cat.length > 0 ? cat : null;
      if (catVal != null) catByRate.set(rate, catVal);
    }
    if (!selectionByRate.has(rate) || selectionByRate.get(rate) == null) {
      const sel = (item as { taxCategorySelection?: string | null }).taxCategorySelection;
      const selVal = typeof sel === 'string' && sel.length > 0 ? sel : null;
      if (selVal != null) selectionByRate.set(rate, selVal);
    }
  }

  // ── (1) Bevorzugt: calculatedTotals.taxSummary (Beträge), Kategorie aus items ──
  const taxSummary = raw.calculatedTotals?.taxSummary;
  if (taxSummary && Object.keys(taxSummary).length > 0) {
    const perRate: PerRateLine[] = [];
    for (const rateKey of Object.keys(taxSummary)) {
      const parsed = Number.parseFloat(rateKey);
      const rate = Number.isFinite(parsed) ? parsed : 0;
      const entry = taxSummary[rateKey];
      perRate.push({
        taxRate: rate,
        netCents: eurosToCents(entry.base ?? 0),
        vatCents: eurosToCents(entry.amount ?? 0),
        taxCategory: catByRate.get(rate) ?? null,
        taxCategorySelection: selectionByRate.get(rate) ?? null,
      });
    }
    return perRate;
  }

  // ── (2) Rekonstruktion aus items[] ──────────────────────────────────────────
  if (items.length === 0) return [];

  // Netto je Satz (Math.round je Position, dann summieren — §4.7).
  const netByRate = new Map<number, number>();
  let totalNetCents = 0;

  for (const item of items) {
    const rate = typeof item.taxRate === 'number' ? item.taxRate : 0;
    const lineNet = Math.round((item.netPrice ?? 0) * (item.quantity ?? 0) * 100);
    netByRate.set(rate, (netByRate.get(rate) ?? 0) + lineNet);
    totalNetCents += lineNet;
  }

  // Dokumentrabatte/-aufschläge (BG-20/21) — Prozent bezieht sich auf Gesamt-Netto
  // (gespiegelt zu calculateTotals), Festbetrag direkt; je `taxRate`-Bucket.
  const allowances = Array.isArray(raw.documentAllowances) ? raw.documentAllowances : [];
  for (const a of allowances) {
    if (!a) continue;
    const absCents =
      a.type === 'percentage'
        ? Math.round((totalNetCents * (a.value ?? 0)) / 100)
        : eurosToCents(a.value ?? 0);
    const adj = a.isCharge ? absCents : -absCents;
    const rate = typeof a.taxRate === 'number' ? a.taxRate : 0;
    netByRate.set(rate, (netByRate.get(rate) ?? 0) + adj);
  }

  const perRate: PerRateLine[] = [];
  netByRate.forEach((netCents, rate) => {
    perRate.push({
      taxRate: rate,
      netCents,
      vatCents: Math.round((netCents * rate) / 100),
      taxCategory: catByRate.get(rate) ?? null,
      taxCategorySelection: selectionByRate.get(rate) ?? null,
    });
  });
  return perRate;
}
